import type { Vetor3 } from '../../dominio/geometria/vetor3'
import type {
  AncoraOcupante,
  ConfiguracaoCinto,
  PerfilVeiculo,
} from '../../dominio/didatico/perfilVeiculo'
import { sufixoAncora } from '../../dominio/didatico/perfilVeiculo'
import { pontosDoCinto } from '../../dominio/didatico/pontosCinto'
import { componenteDidatico } from '../../dominio/didatico/catalogo'
import { CORES_SEMANTICAS } from '../../dominio/didatico/tokens'
import { Barra } from '../componentes/Barra'
import { MaterialPeca, Peca } from '../componentes/Peca'
import { Rotulo3D } from '../componentes/Rotulo3D'
import { useMaquete } from '../componentes/contextoMaquete'

interface Props {
  perfil: PerfilVeiculo
  ancora: AncoraOcupante
  config: ConfiguracaoCinto
}

/**
 * Conjunto de retenção de uma posição de ocupante.
 *
 * A geometria é derivada da âncora e da fila, então a mesma implementação
 * serve dianteiro e traseiro, hatch e sedan. O tipo do cinto e a presença do
 * pretensionador vêm do perfil didático — não são assumidos.
 */
export function CintoSeguranca({ perfil, ancora, config }: Props) {
  const { mostrarRotulos, camadas, retencaoEmEvidencia, semCintoAncoras } = useMaquete()
  // o ocupante está sem cinto: a faixa não é desenhada, mas a peça continua
  // existindo no veículo — não é um estado de `estadoPecas`
  if (semCintoAncoras.includes(ancora.id)) return null
  const sufixo = sufixoAncora(ancora.id)
  const idCinto = `cinto${sufixo}`
  const idPretensionador = `pretensionador${sufixo}`
  const idAncoragens = `ancoragens${sufixo}`

  // a geometria vem do domínio, que é onde ela está sob teste
  const pontos = pontosDoCinto(perfil, ancora, config)
  const espessura = retencaoEmEvidencia ? 0.065 : 0.05
  const faixa = (de: Vetor3, para: Vetor3, chave: string) => (
    <Barra key={chave} id={idCinto} de={de} para={para} largura={espessura} espessura={0.016} />
  )

  return (
    <group>
      <Peca id={idCinto}>
        {/* faixa diagonal: só existe no cinto de três pontos */}
        {pontos.ancoragemSuperior && pontos.ombro && [
          faixa(pontos.ancoragemSuperior, pontos.ombro, 'diagonal-alta'),
          faixa(pontos.ombro, pontos.fecho, 'diagonal-baixa'),
        ]}
        {/* faixa pélvica: existe nos dois tipos */}
        {faixa(pontos.fecho, pontos.quadrilExterno, 'pelvica-interna')}
        {faixa(pontos.quadrilExterno, pontos.ancoragemInferiorExterna, 'pelvica-externa')}
        <mesh position={[pontos.fecho.x, pontos.fecho.y, pontos.fecho.z]}>
          <boxGeometry args={[0.07, 0.05, 0.1]} />
          <MaterialPeca id={idCinto} metalico={0.6} rugosidade={0.3} />
        </mesh>
      </Peca>

      <Peca id={idAncoragens}>
        {[pontos.ancoragemSuperior, pontos.ancoragemInferiorExterna, pontos.ancoragemInferiorInterna]
          .filter((ponto): ponto is Vetor3 => ponto !== null)
          .map((ponto, i) => (
            <mesh key={i} position={[ponto.x, ponto.y, ponto.z]}>
              <sphereGeometry args={[0.035, 12, 10]} />
              <MaterialPeca id={idAncoragens} metalico={0.6} rugosidade={0.3} />
            </mesh>
          ))}
      </Peca>

      {pontos.pretensionador && (
        <Peca id={idPretensionador}>
          <mesh
            position={[pontos.pretensionador.x, pontos.pretensionador.y, pontos.pretensionador.z]}
            rotation={[0, 0, Math.PI / 2]}
          >
            <cylinderGeometry args={[0.055, 0.055, 0.09, 14]} />
            <MaterialPeca id={idPretensionador} metalico={0.5} rugosidade={0.35} />
          </mesh>
          {retencaoEmEvidencia && (
            <mesh position={[pontos.pretensionador.x, pontos.pretensionador.y, pontos.pretensionador.z]}>
              <sphereGeometry args={[0.1, 14, 12]} />
              <meshBasicMaterial color={CORES_SEMANTICAS.retencao} transparent opacity={0.22} />
            </mesh>
          )}
          {mostrarRotulos && camadas.retencao && (
            <RotuloRetencao
              id={idPretensionador}
              posicao={[
                pontos.pretensionador.x * 1.12,
                pontos.pretensionador.y + 0.14,
                pontos.pretensionador.z,
              ]}
            />
          )}
        </Peca>
      )}
    </group>
  )
}

/**
 * Rótulo do pretensionador. Enquanto a abreviação não for confirmada pelo
 * instrutor, mostra o nome por extenso em vez de inventar sigla.
 */
function RotuloRetencao({ id, posicao }: { id: string; posicao: [number, number, number] }) {
  const componente = componenteDidatico(id)
  if (!componente) return null
  return (
    <Rotulo3D
      posicao={posicao}
      texto={componente.sigla ?? 'pretensionador'}
      variante="retencao"
      fator={3.6}
    />
  )
}
